import { createSlice, nanoid } from '@reduxjs/toolkit';

// Coupon type constants
export const COUPON_TYPE = {
  PERCENTAGE: 'percentage',
  FIXED_AMOUNT: 'fixed_amount',
  FREE_RIDE: 'free_ride',
  FIRST_RIDE: 'first_ride'
};

// Coupon status constants
export const COUPON_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  EXPIRED: 'expired',
  USED_UP: 'used_up'
};

// Generate realistic dummy coupon data
const generateDummyCoupons = () => {
  const coupons = [
    {
      id: nanoid(),
      code: 'WELCOME20',
      name: 'Welcome Discount',
      description: '20% off your first ride',
      type: COUPON_TYPE.PERCENTAGE,
      value: 20,
      maxDiscount: 10.00,
      minOrderValue: 5.00,
      usageLimit: 1000,
      usedCount: 234,
      userUsageLimit: 1,
      status: COUPON_STATUS.ACTIVE,
      validFrom: '2024-01-01T00:00:00.000Z',
      validUntil: '2024-12-31T23:59:59.000Z',
      applicableToNewUsers: true,
      applicableToExistingUsers: false,
      applicableRideTypes: ['standard', 'premium'],
      createdAt: '2024-01-01T00:00:00.000Z',
      createdBy: 'admin',
      lastModified: '2024-01-15T10:30:00.000Z'
    },
    {
      id: nanoid(),
      code: 'SAVE5NOW',
      name: 'Quick Save',
      description: '$5 off any ride',
      type: COUPON_TYPE.FIXED_AMOUNT,
      value: 5.00,
      maxDiscount: 5.00,
      minOrderValue: 15.00,
      usageLimit: 500,
      usedCount: 89,
      userUsageLimit: 3,
      status: COUPON_STATUS.ACTIVE,
      validFrom: '2024-11-01T00:00:00.000Z',
      validUntil: '2024-12-31T23:59:59.000Z',
      applicableToNewUsers: true,
      applicableToExistingUsers: true,
      applicableRideTypes: ['standard', 'premium', 'shared'],
      createdAt: '2024-10-25T14:20:00.000Z',
      createdBy: 'admin',
      lastModified: '2024-11-01T09:15:00.000Z'
    },
    {
      id: nanoid(),
      code: 'FREERIDE',
      name: 'Free Ride Friday',
      description: 'One free ride up to $15',
      type: COUPON_TYPE.FREE_RIDE,
      value: 15.00,
      maxDiscount: 15.00,
      minOrderValue: 0,
      usageLimit: 100,
      usedCount: 67,
      userUsageLimit: 1,
      status: COUPON_STATUS.ACTIVE,
      validFrom: '2024-12-01T00:00:00.000Z',
      validUntil: '2024-12-31T23:59:59.000Z',
      applicableToNewUsers: false,
      applicableToExistingUsers: true,
      applicableRideTypes: ['standard'],
      dayOfWeekRestriction: [5], // Friday only
      createdAt: '2024-11-28T16:45:00.000Z',
      createdBy: 'marketing',
      lastModified: '2024-11-28T16:45:00.000Z'
    },
    {
      id: nanoid(),
      code: 'STUDENT15',
      name: 'Student Discount',
      description: '15% off for students',
      type: COUPON_TYPE.PERCENTAGE,
      value: 15,
      maxDiscount: 8.00,
      minOrderValue: 10.00,
      usageLimit: 2000,
      usedCount: 456,
      userUsageLimit: 5,
      status: COUPON_STATUS.ACTIVE,
      validFrom: '2024-09-01T00:00:00.000Z',
      validUntil: '2025-05-31T23:59:59.000Z',
      applicableToNewUsers: true,
      applicableToExistingUsers: true,
      applicableRideTypes: ['standard', 'shared'],
      requiresVerification: true,
      verificationField: 'student_id',
      createdAt: '2024-08-25T11:30:00.000Z',
      createdBy: 'admin',
      lastModified: '2024-09-01T08:00:00.000Z'
    },
    {
      id: nanoid(),
      code: 'EXPIRED10',
      name: 'Old Promotion',
      description: '10% off - expired',
      type: COUPON_TYPE.PERCENTAGE,
      value: 10,
      maxDiscount: 5.00,
      minOrderValue: 20.00,
      usageLimit: 1000,
      usedCount: 1000,
      userUsageLimit: 2,
      status: COUPON_STATUS.EXPIRED,
      validFrom: '2024-06-01T00:00:00.000Z',
      validUntil: '2024-08-31T23:59:59.000Z',
      applicableToNewUsers: true,
      applicableToExistingUsers: true,
      applicableRideTypes: ['standard', 'premium'],
      createdAt: '2024-05-25T09:15:00.000Z',
      createdBy: 'marketing',
      lastModified: '2024-06-01T10:00:00.000Z'
    }
  ];

  return coupons;
};

const initialState = generateDummyCoupons();

const couponSlice = createSlice({
  name: 'coupons',
  initialState,
  reducers: {
    addCoupon: {
      reducer(state, action) {
        state.unshift(action.payload);
      },
      prepare(couponData) {
        return {
          payload: {
            id: nanoid(),
            ...couponData,
            usedCount: 0,
            status: COUPON_STATUS.ACTIVE,
            createdAt: new Date().toISOString(),
            lastModified: new Date().toISOString()
          }
        };
      }
    },

    updateCoupon(state, action) {
      const { id, ...updates } = action.payload;
      const couponIndex = state.findIndex(c => c.id === id);
      if (couponIndex !== -1) {
        state[couponIndex] = {
          ...state[couponIndex],
          ...updates,
          lastModified: new Date().toISOString()
        };
      }
    },

    deleteCoupon(state, action) {
      return state.filter(coupon => coupon.id !== action.payload);
    },

    validateCoupon(state, action) {
      const { code, userId, rideType, orderValue } = action.payload;
      const coupon = state.find(c => c.code.toLowerCase() === code.toLowerCase());

      if (!coupon) {
        return { valid: false, error: 'Coupon not found' };
      }

      // Check if coupon is active
      if (coupon.status !== COUPON_STATUS.ACTIVE) {
        return { valid: false, error: 'Coupon is not active' };
      }

      // Check date validity
      const now = new Date();
      const validFrom = new Date(coupon.validFrom);
      const validUntil = new Date(coupon.validUntil);

      if (now < validFrom || now > validUntil) {
        return { valid: false, error: 'Coupon has expired' };
      }

      // Check usage limits
      if (coupon.usedCount >= coupon.usageLimit) {
        coupon.status = COUPON_STATUS.USED_UP;
        return { valid: false, error: 'Coupon usage limit reached' };
      }

      // Check minimum order value
      if (orderValue < coupon.minOrderValue) {
        return {
          valid: false,
          error: `Minimum order value is $${coupon.minOrderValue.toFixed(2)}`
        };
      }

      // Check ride type applicability
      if (!coupon.applicableRideTypes.includes(rideType)) {
        return { valid: false, error: 'Coupon not applicable to this ride type' };
      }

      // Check day of week restriction
      if (coupon.dayOfWeekRestriction) {
        const currentDay = now.getDay();
        if (!coupon.dayOfWeekRestriction.includes(currentDay)) {
          return { valid: false, error: 'Coupon not valid on this day' };
        }
      }

      return { valid: true, coupon };
    },

    applyCoupon(state, action) {
      const { couponId, userId, tripId } = action.payload;
      const coupon = state.find(c => c.id === couponId);

      if (coupon) {
        coupon.usedCount += 1;

        // Add usage record
        if (!coupon.usageHistory) {
          coupon.usageHistory = [];
        }

        coupon.usageHistory.push({
          userId,
          tripId,
          usedAt: new Date().toISOString()
        });

        // Update status if usage limit reached
        if (coupon.usedCount >= coupon.usageLimit) {
          coupon.status = COUPON_STATUS.USED_UP;
        }
      }
    },

    calculateDiscount(state, action) {
      const { coupon, orderValue } = action.payload;
      let discount = 0;

      switch (coupon.type) {
        case COUPON_TYPE.PERCENTAGE:
          discount = (orderValue * coupon.value) / 100;
          break;
        case COUPON_TYPE.FIXED_AMOUNT:
          discount = coupon.value;
          break;
        case COUPON_TYPE.FREE_RIDE:
          discount = Math.min(orderValue, coupon.value);
          break;
        default:
          discount = 0;
      }

      // Apply maximum discount limit
      if (coupon.maxDiscount && discount > coupon.maxDiscount) {
        discount = coupon.maxDiscount;
      }

      // Ensure discount doesn't exceed order value
      discount = Math.min(discount, orderValue);

      return {
        originalAmount: orderValue,
        discountAmount: discount,
        finalAmount: orderValue - discount,
        couponApplied: coupon.code
      };
    },

    toggleCouponStatus(state, action) {
      const { id } = action.payload;
      const coupon = state.find(c => c.id === id);

      if (coupon && coupon.status !== COUPON_STATUS.EXPIRED && coupon.status !== COUPON_STATUS.USED_UP) {
        coupon.status = coupon.status === COUPON_STATUS.ACTIVE ?
          COUPON_STATUS.INACTIVE : COUPON_STATUS.ACTIVE;
        coupon.lastModified = new Date().toISOString();
      }
    },

    updateCouponUsage(state, action) {
      const { id, usedCount } = action.payload;
      const coupon = state.find(c => c.id === id);

      if (coupon) {
        coupon.usedCount = usedCount;

        // Update status based on usage
        if (usedCount >= coupon.usageLimit) {
          coupon.status = COUPON_STATUS.USED_UP;
        } else if (coupon.status === COUPON_STATUS.USED_UP) {
          coupon.status = COUPON_STATUS.ACTIVE;
        }

        coupon.lastModified = new Date().toISOString();
      }
    }
  }
});

export const {
  addCoupon,
  updateCoupon,
  deleteCoupon,
  validateCoupon,
  applyCoupon,
  calculateDiscount,
  toggleCouponStatus,
  updateCouponUsage
} = couponSlice.actions;

export default couponSlice.reducer;